/* =====================================================================
 * Custom @nuxt/image provider — Pipehouse pre-optimized variants.
 *
 * Returns SITE-RELATIVE local paths (under public/images/opt/) instead of
 * `/_ipx/` URLs, so `nuxt generate` runs NO IPX/sharp pass and the deployed
 * static pipe stays self-contained (images served from the brand site, no
 * token needed by anonymous end-users).
 *
 * The actual variant bytes are pulled into the static output by the
 * post-generate sweep in `lib/bundleOptimizedImages.ts`, which scans the
 * generated HTML for the exact paths this provider emitted and fetches each
 * from the (cache-backed) API `/api/media/<uuid>?w&q&f=webp`. Reading the real
 * output means there is no width-math duplication and no drift.
 *
 * `baseURL` is the deployed RELEASE_PATH (== app.baseURL), mirroring the SVG
 * bundling branch in useImagePreset.ts so images resolve under sub-paths.
 * ===================================================================== */

// Loose typing: @nuxt/image's exported provider types vary across versions;
// the runtime contract (src, { modifiers, baseURL }) -> { url } is stable.
type Modifiers = { width?: number; quality?: number; format?: string }

const MEDIA_UUID_RE = /\/media\/([^/?#]+)/

function getImage(
  src: string,
  { modifiers = {}, baseURL = '/' }: { modifiers?: Modifiers; baseURL?: string } = {},
) {
  const uuid = MEDIA_UUID_RE.exec(src)?.[1] ?? src
  const base = (baseURL || '/').replace(/\/+$/, '')
  const width = modifiers.width
  const quality = modifiers.quality ?? 80
  // Non-original variants are always webp (set by useImagePreset). Original
  // (no width) is served as native-resolution webp at high quality.
  const file = width ? `w${width}-q${quality}.webp` : 'original.webp'
  return { url: `${base}/images/opt/${uuid}/${file}` }
}

// @nuxt/image v2 imports a provider file's DEFAULT export and calls it to get
// the provider object — exactly what its `defineProvider(setup)` helper returns
// (a memoized thunk `() => ({ getImage, supportsAlias })`). Replicated inline
// here to avoid importing a deep, version-fragile `@nuxt/image/dist/...` path.
const provider = { supportsAlias: true, getImage }
export default () => provider
